import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trophy, Sparkles, Loader2 } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const rankStyles = [
  { badge: "bg-gradient-to-br from-amber-400 to-yellow-500 text-white shadow-lg shadow-amber-300/50", label: "🥇" },
  { badge: "bg-gradient-to-br from-slate-300 to-slate-500 text-white", label: "🥈" },
  { badge: "bg-gradient-to-br from-orange-400 to-amber-600 text-white", label: "🥉" },
];

export const LeaderboardWidget = () => {
  const [top, setTop] = useState([]);
  const [prizes, setPrizes] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/leaderboard/affiliates`)
      .then((r) => { setTop(r.data.top); setPrizes(r.data.prizes); })
      .catch((err) => console.error("leaderboard fetch:", err))
      .finally(() => setLoading(false));
  }, []);

  return (
    <section data-testid="home-leaderboard-widget" className="max-w-6xl mx-auto px-4 sm:px-8 py-16">
      <div className="text-center mb-10">
        <p className="inline-flex items-center gap-2 text-xs tracking-[0.2em] font-bold text-emerald-600 uppercase mb-3">
          <Trophy className="w-4 h-4" /> Monthly Leaderboard
        </p>
        <h2 className="text-3xl sm:text-4xl font-heading font-bold text-slate-900">
          This month&apos;s top affiliates
        </h2>
        <p className="mt-3 text-slate-600 text-sm max-w-xl mx-auto">
          Top 3 at the end of each month win real prizes. Ranked by approved affiliate signups.
        </p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Prize pool card */}
        <div className="lg:col-span-1 bg-gradient-to-br from-emerald-600 to-teal-700 text-white rounded-2xl p-6 shadow-xl">
          <p className="text-xs uppercase tracking-wider font-bold text-emerald-100 mb-3">Prize pool this month</p>
          {[
            { icon: "🥇", label: "1st place", prize: prizes?.first },
            { icon: "🥈", label: "2nd place", prize: prizes?.second },
            { icon: "🥉", label: "3rd place", prize: prizes?.third },
          ].map((row) => (
            <div key={row.label} className="border-t border-emerald-500/40 py-3 first:border-t-0">
              <p className="font-semibold text-sm">{row.icon} {row.label}</p>
              {row.prize && (
                <p className="text-xs text-emerald-100 mt-0.5">
                  ${row.prize.gift_card_usd} gift card + {row.prize.bonus_ai_posts} AI posts + {row.prize.exclusive_link_credits} exclusive-link unlocks
                </p>
              )}
            </div>
          ))}
          <Link to="/signup" data-testid="leaderboard-join-cta" className="mt-5 block w-full text-center py-2.5 rounded-full bg-white text-emerald-700 font-bold text-sm hover:bg-emerald-50">
            Sign up + start climbing →
          </Link>
        </div>

        {/* Top 10 list */}
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl p-6">
          {loading ? (
            <div className="py-14 flex justify-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : top.length === 0 ? (
            <div className="py-14 text-center">
              <Sparkles className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 text-sm">Board resets on the 1st. Be the first name here.</p>
              <Link to="/signup" className="mt-4 inline-block px-6 py-2 rounded-full bg-slate-900 text-white text-sm font-bold hover:bg-slate-700">
                Sign up →
              </Link>
            </div>
          ) : (
            <ol className="divide-y divide-slate-100">
              {top.map((row, i) => {
                const style = rankStyles[i];
                return (
                  <li key={row.handle || i} data-testid={`leaderboard-rank-${i + 1}`} className="flex items-center gap-4 py-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-black text-sm flex-shrink-0 ${style ? style.badge : "bg-slate-100 text-slate-500"}`}>
                      {style ? style.label : i + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm text-slate-900 truncate">{row.name || `@${row.handle}`}</p>
                      <p className="text-xs text-slate-500 font-mono">@{row.handle}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-lg text-emerald-700">{row.affiliates_joined_count}</p>
                      <p className="text-[10px] text-slate-500 uppercase tracking-wider">signups</p>
                    </div>
                    {row.is_paid && (
                      <span className="ml-2 text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">MEMBER</span>
                    )}
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      </div>
    </section>
  );
};
